/** Formatting helpers. All inputs are ISO-8601 UTC strings or seconds. */

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** HH:MM:SS (hours can exceed 24). */
export function formatHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
}

/** MM:SS for short countdowns (grace). */
export function formatMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;
}

export function hoursMinutes(totalSeconds: number): { hours: number; minutes: number } {
  const s = Math.max(0, Math.floor(totalSeconds));
  return { hours: Math.floor(s / 3600), minutes: Math.floor((s % 3600) / 60) };
}

export function daysHours(totalSeconds: number): { days: number; hours: number } {
  const s = Math.max(0, Math.floor(totalSeconds));
  return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600) };
}

const localFmt = (locale: string) =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

/** "2026-09-14 18:30" in the browser's zone. */
export function formatLocal(iso: string, locale = "en-CA"): string {
  const d = new Date(iso);
  const parts = localFmt(locale === "zh-TW" ? "zh-TW" : "en-CA").formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}

/** "2026-09-14 10:30 UTC" */
export function formatUtc(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "local";
  }
}

export function secondsUntil(iso: string, nowMs = Date.now()): number {
  return Math.max(0, Math.floor((Date.parse(iso) - nowMs) / 1000));
}
